import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Star, Bell, TrendingUp, TrendingDown, Building2, DollarSign, BarChart3, Users, Globe, Activity, ChevronDown, ChevronUp, ExternalLink, Info, Layers, FastForward, RefreshCw } from 'lucide-react';
import { createChart, IChartApi, ISeriesApi, Time, LineStyle } from 'lightweight-charts';
import eodhd from '../services/eodhd';
import api, { Watchlist } from '../services/api';
import PriceChange from '../components/PriceChange';
import LoadingSpinner from '../components/LoadingSpinner';
import PriceAlerts from '../components/PriceAlerts';
import ChartReplayControls from '../components/ChartReplayControls';
import AIAnalysisHistory from '../components/AIAnalysisHistory';
import AssetNews from '../components/AssetNews';
import { formatLargeNumber } from '../utils/helpers';
import { useRealtimePrice } from '../hooks/useRealtimePrice';
import { useActivityTracker } from '../hooks/useActivityTracker';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermission } from '../hooks/usePermission';
import { expertApi } from '../services/expertApi';
import TradingViewAdvancedChart from '../components/TradingViewAdvancedChart';
import TaAnalysisCard from '../components/TaAnalysisCard';
import { StrategySignalCard, StrategySignalCardSkeleton } from '../components/signals/StrategySignalCard';
import type { AssetData } from '../types';
import type { EnrichedSignal } from '../types/signals';

interface ChartDataPoint {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

interface IndicatorDataPoint {
  time: Time;
  value: number;
}

interface IndicatorSettings {
  sma50: boolean;
  sma200: boolean;
  ema20: boolean;
  bollinger: boolean;
  rsi: boolean;
  macd: boolean;
}

interface StatRowProps {
  label: string;
  value: string | number;
  highlight?: boolean;
  tooltip?: string;
}

interface FundamentalData {
  General?: {
    Name?: string;
    Description?: string;
    Sector?: string;
    Industry?: string;
    Exchange?: string;
    Currency?: string;
    CountryName?: string;
    FullTimeEmployees?: number;
    WebURL?: string;
    LogoURL?: string;
    IPODate?: string;
  };
  Highlights?: {
    MarketCapitalization?: number;
    MarketCapitalizationMln?: number;
    PERatio?: number;
    PEGRatio?: number;
    EarningsShare?: number;
    DividendYield?: number;
    DividendShare?: number;
    BookValue?: number;
    ProfitMargin?: number;
    OperatingMarginTTM?: number;
    ReturnOnAssetsTTM?: number;
    ReturnOnEquityTTM?: number;
    RevenueTTM?: number;
    RevenuePerShareTTM?: number;
    GrossProfitTTM?: number;
    EBITDA?: number;
    QuarterlyEarningsGrowthYOY?: number;
    QuarterlyRevenueGrowthYOY?: number;
    EPSEstimateCurrentYear?: number;
    EPSEstimateNextYear?: number;
    WallStreetTargetPrice?: number;
  };
  Valuation?: {
    TrailingPE?: number;
    ForwardPE?: number;
    PriceSalesTTM?: number;
    PriceBookMRQ?: number;
    EnterpriseValueRevenue?: number;
    EnterpriseValueEbitda?: number;
  };
  Technicals?: {
    Beta?: number;
    '52WeekHigh'?: number;
    '52WeekLow'?: number;
    '50DayMA'?: number;
    '200DayMA'?: number;
    SharesShort?: number;
    ShortRatio?: number;
    ShortPercent?: number;
  };
  SharesStats?: {
    SharesOutstanding?: number;
    SharesFloat?: number;
    PercentInsiders?: number;
    PercentInstitutions?: number;
  };
  AnalystRatings?: {
    Rating?: number;
    TargetPrice?: number;
    StrongBuy?: number;
    Buy?: number;
    Hold?: number;
    Sell?: number;
    StrongSell?: number;
  };
}

interface TechnicalIndicators {
  rsi?: number;
  macd?: { macd: number; signal: number; histogram: number };
  sma50?: number;
  sma200?: number;
  signal?: 'bullish' | 'bearish' | 'neutral';
}

const StatRow: React.FC<StatRowProps> = ({ label, value, highlight, tooltip }) => (
  <div className="flex justify-between py-2 border-b border-slate-700/50 last:border-0 group">
    <span className="text-sm text-slate-400 flex items-center gap-1">
      {label}
      {tooltip && (
        <span className="relative">
          <Info size={12} className="text-slate-500 cursor-help" />
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-700 text-xs text-slate-300 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
            {tooltip}
          </span>
        </span>
      )}
    </span>
    <span className={`font-mono text-sm ${highlight ? 'text-teal-400 font-semibold' : 'text-slate-200'}`}>
      {value}
    </span>
  </div>
);

const AssetDetail: React.FC = () => {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const routerStrategyBreakdown =
    (location.state as { strategy_breakdown?: EnrichedSignal } | null)
      ?.strategy_breakdown ?? null;

  const { allowed: hasStrategyFeature } = usePermission('feature:strategy-driven-signals');

  // Chart provider preference (v30-1-6)
  const { data: bankerPrefs } = useQuery({
    queryKey: ['bankerPreferences'],
    queryFn: () => expertApi.getBankerPreferences(),
    staleTime: 5 * 60 * 1000,
  });
  const isTV = bankerPrefs?.chart_provider === 'tradingview';

  const queryClient = useQueryClient();
  const {
    data: fetchedStrategyBreakdown,
    isLoading: strategyBreakdownLoading,
    isError: strategyBreakdownError,
  } = useQuery<EnrichedSignal>({
    queryKey: ['strategy-breakdown', symbol],
    queryFn: () => api.getStrategyBreakdown(symbol!),
    enabled: !!symbol && hasStrategyFeature && routerStrategyBreakdown === null,
    staleTime: 15 * 60 * 1000,
    retry: 1,
  });

  const [strategyRefreshing, setStrategyRefreshing] = useState(false);
  const forceRefreshStrategyBreakdown = async () => {
    if (!symbol) return;
    try {
      setStrategyRefreshing(true);
      const fresh = await api.getStrategyBreakdown(symbol, { force: true });
      queryClient.setQueryData(['strategy-breakdown', symbol], fresh);
    } finally {
      setStrategyRefreshing(false);
    }
  };

  const strategyBreakdown: EnrichedSignal | null =
    routerStrategyBreakdown ?? fetchedStrategyBreakdown ?? null;

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const bidLineRef = useRef<any>(null);
  const askLineRef = useRef<any>(null);
  const sma50SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const sma200SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const ema20SeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerUpperRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerLowerRef = useRef<ISeriesApi<'Line'> | null>(null);
  const bollingerMiddleRef = useRef<ISeriesApi<'Line'> | null>(null);
  const rsiSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdLineRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdSignalRef = useRef<ISeriesApi<'Line'> | null>(null);
  const macdHistogramRef = useRef<ISeriesApi<'Histogram'> | null>(null);

  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [fundamentalsLoading, setFundamentalsLoading] = useState(true);
  const [assetData, setAssetData] = useState<AssetData | null>(null);
  const [fundamentals, setFundamentals] = useState<FundamentalData | null>(null);
  const [technicals, setTechnicals] = useState<TechnicalIndicators | null>(null);
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [sessionHigh, setSessionHigh] = useState<number | null>(null);
  const [sessionLow, setSessionLow] = useState<number | null>(null);
  const [timeframe, setTimeframe] = useState<'5M' | '15M' | '30M' | '1H' | '1D' | '1W' | '1M' | '3M' | '1Y'>('1D');
  const [indicatorSettings, setIndicatorSettings] = useState<IndicatorSettings>({
    sma50: true,
    sma200: true,
    ema20: false,
    bollinger: false,
    rsi: true,
    macd: false,
  });
  const [showIndicatorMenu, setShowIndicatorMenu] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    fundamentals: true,
    valuation: true,
    technicals: true,
    profile: false,
    analysts: true,
  });
  const [isLiveChart, setIsLiveChart] = useState(false);
  const [chartMode, setChartMode] = useState<'time' | 'tick'>('time');
  const [ticksPerCandle, setTicksPerCandle] = useState(10);
  const [replayMode, setReplayMode] = useState(false);
  const [historicalChartData, setHistoricalChartData] = useState<ChartDataPoint[]>([]);
  const lastCandleRef = useRef<ChartDataPoint | null>(null);
  const tickDataRef = useRef<ChartDataPoint[]>([]);
  const currentTickCountRef = useRef(0);
  const tickCandleRef = useRef<ChartDataPoint | null>(null);
  // Tracks bar duration in seconds for live updates; null = daily (string Time) mode
  const barDurationSecondsRef = useRef<number | null>(null);

  // Subscribe to real-time price updates via WebSocket
  const livePrice = useRealtimePrice(symbol || '');

  const { trackEvent } = useActivityTracker();
  const chartMountTimeRef = useRef<number>(Date.now());
  const timeframeRef = useRef(timeframe);

  // Keep timeframeRef current so the unmount cleanup always has the latest value
  useEffect(() => {
    timeframeRef.current = timeframe;
  }, [timeframe]);

  // Track chart_view on unmount if viewed for ≥5 seconds (AC6)
  useEffect(() => {
    chartMountTimeRef.current = Date.now();
    return () => {
      const durationSeconds = Math.floor((Date.now() - chartMountTimeRef.current) / 1000);
      if (durationSeconds >= 5 && symbol) {
        trackEvent('chart_view', symbol, {
          timeframe: timeframeRef.current,
          duration_seconds: durationSeconds,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const timeframes = [
    { id: '5M',  label: '5M'  },
    { id: '15M', label: '15M' },
    { id: '30M', label: '30M' },
    { id: '1H',  label: '1H'  },
    { id: '1D',  label: '1D'  },
    { id: '1W',  label: '1W'  },
    { id: '1M',  label: '1M'  },
    { id: '3M',  label: '3M'  },
    { id: '1Y',  label: '1Y'  },
  ] as const;

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleIndicator = (indicator: keyof IndicatorSettings) => {
    setIndicatorSettings(prev => ({ ...prev, [indicator]: !prev[indicator] }));
  };

  // Calculate SMA from price data
  const calculateSMA = (data: ChartDataPoint[], period: number): IndicatorDataPoint[] => {
    const result: IndicatorDataPoint[] = [];
    for (let i = period - 1; i < data.length; i++) {
      let sum = 0;
      for (let j = 0; j < period; j++) {
        sum += data[i - j].close;
      }
      result.push({ time: data[i].time, value: sum / period });
    }
    return result;
  };

  // Calculate EMA from price data
  const calculateEMA = (data: ChartDataPoint[], period: number): IndicatorDataPoint[] => {
    const result: IndicatorDataPoint[] = [];
    const multiplier = 2 / (period + 1);
    let ema = data[0].close;

    for (let i = 0; i < data.length; i++) {
      ema = (data[i].close - ema) * multiplier + ema;
      if (i >= period - 1) {
        result.push({ time: data[i].time, value: ema });
      }
    }
    return result;
  };

  // Calculate RSI from price data
  const calculateRSI = (data: ChartDataPoint[], period: number = 14): IndicatorDataPoint[] => {
    const result: IndicatorDataPoint[] = [];
    const gains: number[] = [];
    const losses: number[] = [];

    for (let i = 1; i < data.length; i++) {
      const change = data[i].close - data[i - 1].close;
      gains.push(change > 0 ? change : 0);
      losses.push(change < 0 ? -change : 0);

      if (i >= period) {
        const avgGain = gains.slice(i - period, i).reduce((a, b) => a + b, 0) / period;
        const avgLoss = losses.slice(i - period, i).reduce((a, b) => a + b, 0) / period;
        const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
        const rsi = 100 - (100 / (1 + rs));
        result.push({ time: data[i].time, value: rsi });
      }
    }
    return result;
  };

  // Calculate MACD from price data
  const calculateMACD = (data: ChartDataPoint[]): {
    macdLine: IndicatorDataPoint[];
    signalLine: IndicatorDataPoint[];
    histogram: { time: Time; value: number; color: string }[];
  } => {
    const ema12 = calculateEMA(data, 12);
    const ema26 = calculateEMA(data, 26);
    const macdLine: IndicatorDataPoint[] = [];
    const macdValues: number[] = [];

    // Calculate MACD line (EMA12 - EMA26)
    const startIndex = 26 - 12; // Align the arrays
    for (let i = 0; i < ema26.length; i++) {
      const macdValue = ema12[i + startIndex].value - ema26[i].value;
      macdLine.push({ time: ema26[i].time, value: macdValue });
      macdValues.push(macdValue);
    }

    // Calculate Signal line (9-period EMA of MACD)
    const signalLine: IndicatorDataPoint[] = [];
    const multiplier = 2 / 10;
    let signal = macdValues[0];

    for (let i = 0; i < macdLine.length; i++) {
      signal = (macdValues[i] - signal) * multiplier + signal;
      if (i >= 8) {
        signalLine.push({ time: macdLine[i].time, value: signal });
      }
    }

    // Calculate Histogram
    const histogram: { time: Time; value: number; color: string }[] = [];
    for (let i = 8; i < macdLine.length; i++) {
      const histValue = macdLine[i].value - signalLine[i - 8].value;
      histogram.push({
        time: macdLine[i].time,
        value: histValue,
        color: histValue >= 0 ? '#10B981' : '#EF4444',
      });
    }

    return { macdLine: macdLine.slice(8), signalLine, histogram };
  };

  // Calculate Bollinger Bands
  const calculateBollingerBands = (data: ChartDataPoint[], period: number = 20, stdDev: number = 2): {
    upper: IndicatorDataPoint[];
    middle: IndicatorDataPoint[];
    lower: IndicatorDataPoint[];
  } => {
    const middle = calculateSMA(data, period);
    const upper: IndicatorDataPoint[] = [];
    const lower: IndicatorDataPoint[] = [];

    for (let i = period - 1; i < data.length; i++) {
      let sum = 0;
      for (let j = 0; j < period; j++) {
        sum += Math.pow(data[i - j].close - middle[i - period + 1].value, 2);
      }
      const std = Math.sqrt(sum / period);
      const idx = i - period + 1;
      upper.push({ time: middle[idx].time, value: middle[idx].value + stdDev * std });
      lower.push({ time: middle[idx].time, value: middle[idx].value - stdDev * std });
    }

    return { upper, middle, lower };
  };

  useEffect(() => {
    if (symbol) {
      loadAssetData();
      loadFundamentals();
      loadTechnicals();
      loadWatchlists();
    }
  }, [symbol]);

  // Initialize session high/low when assetData loads
  useEffect(() => {
    if (assetData) {
      setSessionHigh(assetData.stats.high);
      setSessionLow(assetData.stats.low);
    }
  }, [assetData]);

  // Update session high/low with real-time WebSocket prices
  useEffect(() => {
    if (!livePrice.isLive || !livePrice.price) return;

    const currentPrice = Number(livePrice.price);
    setSessionHigh(prev => prev === null ? currentPrice : Math.max(prev, currentPrice));
    setSessionLow(prev => prev === null ? currentPrice : Math.min(prev, currentPrice));
  }, [livePrice.price, livePrice.isLive]);

  // Load watchlists from backend and check if current asset is watchlisted
  const loadWatchlists = async () => {
    try {
      const lists = await api.getWatchlists();
      setWatchlists(lists);

      // Check if current symbol is in any watchlist
      const isInWatchlist = lists.some(list =>
        list.items.some(item => item.symbol === symbol)
      );
      setIsWatchlisted(isInWatchlist);
    } catch (error) {
      console.error('Error loading watchlists:', error);
    }
  };

  // Toggle watchlist - save to backend database
  const toggleWatchlistStatus = async () => {
    if (!symbol) return;

    try {
      if (isWatchlisted) {
        // Remove from watchlist
        for (const list of watchlists) {
          const item = list.items.find(i => i.symbol === symbol);
          if (item) {
            await api.removeWatchlistItem(list.id, item.id);
          }
        }
        setIsWatchlisted(false);
      } else {
        // Add to watchlist
        let defaultWatchlist = watchlists.find(w => w.name === 'Main Watchlist') || watchlists[0];

        if (!defaultWatchlist) {
          // Create "Main Watchlist" if none exists
          defaultWatchlist = await api.createWatchlist('Main Watchlist');
          setWatchlists([defaultWatchlist]);
        }

        // Determine exchange from symbol
        const exchange = symbol.includes('JSE') ? 'JSE' :
                        symbol.includes('.CC') ? 'Crypto' :
                        symbol.includes('.FOREX') ? 'Forex' : 'US';

        await api.addWatchlistItem(
          defaultWatchlist.id,
          symbol,
          assetData?.name || symbol.split('.')[0],
          exchange
        );
        setIsWatchlisted(true);
      }

      // Reload watchlists to sync with backend
      await loadWatchlists();
    } catch (error) {
      console.error('Error toggling watchlist:', error);
    }
  };

  const loadAssetData = async () => {
    try {
      setLoading(true);
      const fullSymbol = symbol?.includes('.') ? symbol : `${symbol}.US`;
      const data = await eodhd.getRealTimePrice(fullSymbol);

      setAssetData({
        symbol: symbol || data.code || '',
        name: (symbol || '').replace('.', ' '),
        exchange: fullSymbol.includes('JSE') ? 'JSE' : fullSymbol.includes('.CC') ? 'Crypto' : 'NASDAQ',
        sector: 'Technology',
        price: data.close || 0,
        change: data.change || 0,
        changePercent: data.change_percent ?? data.change_p ?? 0,
        currency: fullSymbol.includes('JSE') ? 'R' : '$',
        stats: {
          open: data.open || data.close,
          high: data.high || data.close * 1.02,
          low: data.low || data.close * 0.98,
          prevClose: data.previousClose || data.close * 0.99,
          week52High: data.close * 1.15,
          week52Low: data.close * 0.75,
          marketCap: '—',
          pe: 0,
          volume: formatLargeNumber(data.volume || 0),
          avgVolume: '—',
        },
      });
    } catch (error) {
      console.error('Error loading asset data:', error);
      setAssetData({
        symbol: symbol || '',
        name: (symbol || '').replace('.', ' '),
        exchange: 'US',
        sector: 'Technology',
        price: 100,
        change: 2.5,
        changePercent: 2.5,
        currency: '$',
        stats: {
          open: 98,
          high: 102,
          low: 97,
          prevClose: 97.5,
          week52High: 115,
          week52Low: 75,
          marketCap: '1.2B',
          pe: 18.5,
          volume: '2.1M',
          avgVolume: '1.8M',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFundamentals = async () => {
    if (!symbol) return;
    try {
      setFundamentalsLoading(true);
      const fullSymbol = symbol.includes('.') ? symbol : `${symbol}.US`;
      const data = await eodhd.getFundamentals(fullSymbol) as FundamentalData;
      setFundamentals(data);

      // Update asset data with fundamentals
      if (data?.General || data?.Highlights) {
        setAssetData(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            name: data.General?.Name || prev.name,
            sector: data.General?.Sector || prev.sector,
            exchange: data.General?.Exchange || prev.exchange,
            stats: {
              ...prev.stats,
              marketCap: data.Highlights?.MarketCapitalizationMln
                ? formatLargeNumber(data.Highlights.MarketCapitalizationMln * 1000000)
                : prev.stats.marketCap,
              pe: data.Highlights?.PERatio || prev.stats.pe,
              week52High: data.Technicals?.['52WeekHigh'] || prev.stats.week52High,
              week52Low: data.Technicals?.['52WeekLow'] || prev.stats.week52Low,
            },
          };
        });
      }
    } catch (error) {
      console.error('Error loading fundamentals:', error);
    } finally {
      setFundamentalsLoading(false);
    }
  };

  const loadTechnicals = async () => {
    if (!symbol) return;
    try {
      const fullSymbol = symbol.includes('.') ? symbol : `${symbol}.US`;

      // Single call — backend returns all indicators in one unified response
      const response = await eodhd.getTechnicalIndicator(fullSymbol, 'all') as {
        indicators?: {
          rsi?: number;
          macd?: { macd: number; signal: number; histogram: number };
          moving_averages?: { sma_50?: number; sma_200?: number };
        };
      };

      const raw = response?.indicators ?? {};
      const indicators: TechnicalIndicators = {};

      if (raw.rsi != null) {
        indicators.rsi = raw.rsi;
      }

      if (raw.macd) {
        indicators.macd = {
          macd: raw.macd.macd,
          signal: raw.macd.signal,
          histogram: raw.macd.histogram ?? (raw.macd.macd - raw.macd.signal),
        };
      }

      if (raw.moving_averages) {
        indicators.sma50 = raw.moving_averages.sma_50;
        indicators.sma200 = raw.moving_averages.sma_200;
      }

      // Calculate overall signal
      let bullishSignals = 0;
      let bearishSignals = 0;

      if (indicators.rsi != null) {
        if (indicators.rsi < 30) bullishSignals++; // Oversold = buy signal
        else if (indicators.rsi > 70) bearishSignals++; // Overbought = sell signal
      }

      if (indicators.macd) {
        if (indicators.macd.histogram > 0) bullishSignals++;
        else bearishSignals++;
      }

      if (indicators.sma50 && indicators.sma200) {
        if (indicators.sma50 > indicators.sma200) bullishSignals++; // Golden cross
        else bearishSignals++; // Death cross
      }

      indicators.signal = bullishSignals > bearishSignals ? 'bullish' : bearishSignals > bullishSignals ? 'bearish' : 'neutral';

      setTechnicals(indicators);
    } catch (error) {
      console.error('Error loading technicals:', error);
    }
  };

  // Chart initialization
  useEffect(() => {
    if (!assetData || !chartContainerRef.current) return;
    if (isTV) return; // TradingView widget handles its own lifecycle

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    }

    const initChart = () => {
      if (!chartContainerRef.current) return;

      const containerWidth = chartContainerRef.current.clientWidth || 600;
      const containerHeight = chartContainerRef.current.clientHeight || 192;

      chartRef.current = createChart(chartContainerRef.current, {
        layout: {
          background: { color: '#1E293B' },
          textColor: '#94A3B8',
        },
        grid: {
          vertLines: { color: '#334155' },
          horzLines: { color: '#334155' },
        },
        crosshair: {
          mode: 1,
        },
        rightPriceScale: {
          borderColor: '#334155',
        },
        timeScale: {
          borderColor: '#334155',
          timeVisible: true,
        },
        width: containerWidth,
        height: containerHeight,
      });

      candleSeriesRef.current = chartRef.current.addCandlestickSeries({
        upColor: '#10B981',
        downColor: '#EF4444',
        borderDownColor: '#EF4444',
        borderUpColor: '#10B981',
        wickDownColor: '#EF4444',
        wickUpColor: '#10B981',
      });

      // Add volume histogram — confined to bottom 15% so it doesn't obscure candles
      volumeSeriesRef.current = chartRef.current.addHistogramSeries({
        color: '#26a69a',
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: 'volume',
      });
      chartRef.current.priceScale('volume').applyOptions({
        scaleMargins: { top: 0.85, bottom: 0 },
      });

      // Add SMA50 line (blue)
      sma50SeriesRef.current = chartRef.current.addLineSeries({
        color: '#3B82F6',
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        priceLineVisible: false,
        lastValueVisible: false,
      });

      // Add SMA200 line (orange)
      sma200SeriesRef.current = chartRef.current.addLineSeries({
        color: '#F97316',
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        priceLineVisible: false,
        lastValueVisible: false,
      });

      // Add EMA20 line (purple)
      ema20SeriesRef.current = chartRef.current.addLineSeries({
        color: '#A855F7',
        lineWidth: 1,
        lineStyle: LineStyle.Solid,
        priceLineVisible: false,
        lastValueVisible: false,
      });

      // Add Bollinger Bands (cyan with transparency)
      bollingerUpperRef.current = chartRef.current.addLineSeries({
        color: 'rgba(34, 211, 238, 0.6)',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
      });

      bollingerMiddleRef.current = chartRef.current.addLineSeries({
        color: 'rgba(34, 211, 238, 0.8)',
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        priceLineVisible: false,
        lastValueVisible: false,
      });

      bollingerLowerRef.current = chartRef.current.addLineSeries({
        color: 'rgba(34, 211, 238, 0.6)',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
      });

      loadChartData();
    };

    const timer = setTimeout(initChart, 50);

    const handleResize = () => {
      if (chartRef.current && chartContainerRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight || 192,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candleSeriesRef.current = null;
        volumeSeriesRef.current = null;
      }
    };
  }, [assetData]);

  useEffect(() => {
    if (candleSeriesRef.current && assetData) {
      loadChartData();
    }
  }, [timeframe]);

  // Helper: aggregate N consecutive 5m bars into a single wider bar (15m, 30m, etc.)
  const aggregateBars = (bars: ChartDataPoint[], groupSize: number): ChartDataPoint[] => {
    const result: ChartDataPoint[] = [];
    for (let i = 0; i < bars.length; i += groupSize) {
      const group = bars.slice(i, i + groupSize);
      if (group.length === 0) continue;
      result.push({
        time: group[0].time,
        open: group[0].open,
        high: Math.max(...group.map((b) => b.high)),
        low: Math.min(...group.map((b) => b.low)),
        close: group[group.length - 1].close,
        volume: group.reduce((sum, b) => sum + (b.volume || 0), 0),
      });
    }
    return result;
  };

  // Helper: load daily bars from EODHD historical API
  const loadDailyBars = async (fullSymbol: string, days: number): Promise<ChartDataPoint[]> => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - days);
    const data = await eodhd.getHistoricalData(fullSymbol, {
      from: fromDate.toISOString().split('T')[0],
      to: toDate.toISOString().split('T')[0],
    });
    // JSE prices are quoted in cents (ZAc) — convert to rands (ZAR)
    const scale = fullSymbol.endsWith('.JSE') ? 0.01 : 1;
    // API returns oldest-first (ascending) — no reverse needed
    return (data || []).map((d) => ({
      time: d.date as Time,
      open: d.open * scale,
      high: d.high * scale,
      low: d.low * scale,
      close: d.close * scale,
      volume: d.volume,
    }));
  };

  const loadChartData = async () => {
    if (!symbol) return;

    try {
      setChartLoading(true);
      lastCandleRef.current = null; // reset live tracking on reload
      const fullSymbol = symbol.includes('.') ? symbol : `${symbol}.US`;

      let chartData: ChartDataPoint[] = [];

      // Intraday timeframes — fetch from /intraday/ endpoint
      // 5M / 15M / 30M use 5m raw bars (15M & 30M are client-aggregated)
      // 1H / 1D use native 1h bars; 1W uses native 1h bars over 7 days
      const intradayTimeframes = ['5M', '15M', '30M', '1H', '1D', '1W'] as const;
      type IntradayTF = typeof intradayTimeframes[number];

      if ((intradayTimeframes as readonly string[]).includes(timeframe)) {
        // ── Intraday path ──────────────────────────────────────
        const nowSec = Math.floor(Date.now() / 1000);

        if (fullSymbol.endsWith('.JSE')) {
          // ── JSE: use stored IRESS 15-min bars ─────────────────
          // EODHD has no JSE intraday coverage; bars come from our jse_bar_recorder job.
          // 5M/15M → raw 15m bars; 30M → 2× aggregate; 1H → 4× aggregate
          const jseTfConfig: Record<IntradayTF, { windowDays: number; barSeconds: number; aggregateBy?: number }> = {
            '5M':  { windowDays: 5,  barSeconds: 900  },
            '15M': { windowDays: 10, barSeconds: 900  },
            '30M': { windowDays: 15, barSeconds: 1800, aggregateBy: 2 },
            '1H':  { windowDays: 30, barSeconds: 3600, aggregateBy: 4 },
            '1D':  { windowDays: 1,  barSeconds: 900  }, // today's session
            '1W':  { windowDays: 7,  barSeconds: 900  },
          };
          const { windowDays, barSeconds, aggregateBy } = jseTfConfig[timeframe as IntradayTF];
          const fromSec = timeframe === '1D'
            ? (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return Math.floor(d.getTime() / 1000); })()
            : nowSec - windowDays * 24 * 60 * 60;

          const jseBars = await eodhd.getJseBars(fullSymbol, { from: fromSec, to: nowSec });

          if (jseBars && jseBars.length > 0) {
            const sorted = [...jseBars].sort((a, b) => a.timestamp - b.timestamp);
            // JSE bars are stored in cents (ZAc) — convert to rands (ZAR)
            let bars: ChartDataPoint[] = sorted.map((d) => ({
              time: d.timestamp as unknown as Time,
              open: d.open * 0.01,
              high: d.high * 0.01,
              low: d.low * 0.01,
              close: d.close * 0.01,
              volume: d.volume,
            }));
            if (aggregateBy) bars = aggregateBars(bars, aggregateBy);
            chartData = bars;
            barDurationSecondsRef.current = barSeconds;
          } else {
            // Cold start: no bars collected yet — fall back to daily bars
            barDurationSecondsRef.current = null;
            const fallbackDays: Record<string, number> = {
              '5M': 5, '15M': 10, '30M': 15, '1H': 30, '1D': 5, '1W': 7,
            };
            chartData = await loadDailyBars(fullSymbol, fallbackDays[timeframe] ?? 5);
          }
        } else {
          // ── Non-JSE: use EODHD intraday ───────────────────────
          const tfConfig: Record<IntradayTF, { interval: string; windowDays: number; barSeconds: number; aggregateBy?: number }> = {
            '5M':  { interval: '5m', windowDays: 5,  barSeconds: 300  },
            '15M': { interval: '5m', windowDays: 10, barSeconds: 900,  aggregateBy: 3 },
            '30M': { interval: '5m', windowDays: 15, barSeconds: 1800, aggregateBy: 6 },
            '1H':  { interval: '1h', windowDays: 30, barSeconds: 3600 },
            '1D':  { interval: '5m', windowDays: 1,  barSeconds: 300  }, // today's session, 5m bars
            '1W':  { interval: '1h', windowDays: 7,  barSeconds: 3600 },
          };
          const { interval, windowDays, barSeconds, aggregateBy } = tfConfig[timeframe as IntradayTF];
          const fromSec = timeframe === '1D'
            ? (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return Math.floor(d.getTime() / 1000); })()
            : nowSec - windowDays * 24 * 60 * 60;

          const intradayData = await eodhd.getIntradayData(fullSymbol, interval, {
            from: fromSec,
            to: nowSec,
          });

          if (intradayData && intradayData.length > 0) {
            const sorted = [...intradayData].sort((a, b) => a.timestamp - b.timestamp);
            let bars: ChartDataPoint[] = sorted.map((d) => ({
              time: d.timestamp as unknown as Time,
              open: d.open,
              high: d.high,
              low: d.low,
              close: d.close,
              volume: d.volume,
            }));
            if (aggregateBy) bars = aggregateBars(bars, aggregateBy);
            chartData = bars;
            barDurationSecondsRef.current = barSeconds;
          } else {
            // Fallback: no intraday data — load proportional daily bars per timeframe
            barDurationSecondsRef.current = null;
            const fallbackDays: Record<string, number> = {
              '5M': 5, '15M': 10, '30M': 15, '1H': 30, '1D': 5, '1W': 7,
            };
            chartData = await loadDailyBars(fullSymbol, fallbackDays[timeframe] ?? 5);
          }
        }
      } else {
        // ── Daily path (1M / 3M / 1Y) ─────────────────────────
        const days = timeframe === '1M' ? 30 : timeframe === '3M' ? 90 : 365;
        chartData = await loadDailyBars(fullSymbol, days);
        barDurationSecondsRef.current = null; // daily = string Time mode
      }

      if (chartData.length > 0 && candleSeriesRef.current) {
        setHistoricalChartData(chartData);
        candleSeriesRef.current.setData(chartData);

        if (volumeSeriesRef.current) {
          const volumeData = chartData.map((d, i) => {
            const prevClose = i > 0 ? chartData[i - 1].close : d.open;
            return {
              time: d.time,
              value: d.volume || 0,
              color: d.close >= prevClose ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)',
            };
          });
          volumeSeriesRef.current.setData(volumeData);
        }

        updateIndicatorOverlays(chartData);
        chartRef.current?.timeScale().fitContent();
      }
    } catch (error) {
      console.error('Error loading chart data:', error);

      if (candleSeriesRef.current) {
        const mockData: ChartDataPoint[] = [];
        let price = assetData?.price || 100;

        const days = ['5M','15M','30M','1H'].includes(timeframe) ? 1 : timeframe === '1D' ? 1 : timeframe === '1W' ? 7 : timeframe === '1M' ? 30 : timeframe === '3M' ? 90 : 365;
        for (let i = days; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const change = (Math.random() - 0.5) * 5;
          const open = price;
          price = price + change;
          const close = price;
          const high = Math.max(open, close) + Math.random() * 2;
          const low = Math.min(open, close) - Math.random() * 2;

          mockData.push({
            time: date.toISOString().split('T')[0] as Time,
            open,
            high,
            low,
            close,
          });
        }

        candleSeriesRef.current.setData(mockData);
        chartRef.current?.timeScale().fitContent();
      }
    } finally {
      setChartLoading(false);
    }
  };

  // Update indicator overlays based on settings
  const updateIndicatorOverlays = (chartData: ChartDataPoint[]) => {
    if (chartData.length < 50) return; // Need enough data for indicators

    // SMA50
    if (indicatorSettings.sma50 && sma50SeriesRef.current) {
      const sma50Data = calculateSMA(chartData, 50);
      sma50SeriesRef.current.setData(sma50Data);
    } else if (sma50SeriesRef.current) {
      sma50SeriesRef.current.setData([]);
    }

    // SMA200
    if (indicatorSettings.sma200 && sma200SeriesRef.current && chartData.length >= 200) {
      const sma200Data = calculateSMA(chartData, 200);
      sma200SeriesRef.current.setData(sma200Data);
    } else if (sma200SeriesRef.current) {
      sma200SeriesRef.current.setData([]);
    }

    // EMA20
    if (indicatorSettings.ema20 && ema20SeriesRef.current) {
      const ema20Data = calculateEMA(chartData, 20);
      ema20SeriesRef.current.setData(ema20Data);
    } else if (ema20SeriesRef.current) {
      ema20SeriesRef.current.setData([]);
    }

    // Bollinger Bands
    if (indicatorSettings.bollinger && bollingerUpperRef.current && bollingerMiddleRef.current && bollingerLowerRef.current) {
      const bollinger = calculateBollingerBands(chartData, 20, 2);
      bollingerUpperRef.current.setData(bollinger.upper);
      bollingerMiddleRef.current.setData(bollinger.middle);
      bollingerLowerRef.current.setData(bollinger.lower);
    } else {
      bollingerUpperRef.current?.setData([]);
      bollingerMiddleRef.current?.setData([]);
      bollingerLowerRef.current?.setData([]);
    }
  };

  // Effect to update indicators when settings change
  useEffect(() => {
    if (candleSeriesRef.current && assetData) {
      loadChartData();
    }
  }, [indicatorSettings]);

  // Effect to update chart with real-time WebSocket prices
  useEffect(() => {
    if (!candleSeriesRef.current || !livePrice.price || !livePrice.isLive) {
      setIsLiveChart(false);
      return;
    }

    setIsLiveChart(true);

    const currentPrice = Number(livePrice.price);

    if (chartMode === 'time') {
      let time: Time;

      if (barDurationSecondsRef.current !== null) {
        // Intraday mode — use Unix seconds rounded to current bar boundary
        const nowSec = Math.floor(Date.now() / 1000);
        const dur = barDurationSecondsRef.current;
        time = (Math.floor(nowSec / dur) * dur) as unknown as Time;
      } else {
        // Daily mode — use today's date string to update today's bar
        time = new Date().toISOString().split('T')[0] as Time;
      }

      if (!lastCandleRef.current || lastCandleRef.current.time !== time) {
        lastCandleRef.current = {
          time,
          open: currentPrice,
          high: currentPrice,
          low: currentPrice,
          close: currentPrice,
        };
      } else {
        lastCandleRef.current = {
          ...lastCandleRef.current,
          high: Math.max(lastCandleRef.current.high, currentPrice),
          low: Math.min(lastCandleRef.current.low, currentPrice),
          close: currentPrice,
        };
      }

      try {
        candleSeriesRef.current.update(lastCandleRef.current);
      } catch (error) {
        console.error('Error updating live candle:', error);
      }
    } else if (chartMode === 'tick') {
      // Tick-based chart: each price update is a tick
      const tickTime = Math.floor(Date.now() / 1000) as Time;

      if (!tickCandleRef.current) {
        // Start first tick candle
        tickCandleRef.current = {
          time: tickTime,
          open: currentPrice,
          high: currentPrice,
          low: currentPrice,
          close: currentPrice,
        };
        currentTickCountRef.current = 1;
      } else {
        // Update current tick candle
        currentTickCountRef.current++;
        tickCandleRef.current = {
          ...tickCandleRef.current,
          time: tickTime,
          high: Math.max(tickCandleRef.current.high, currentPrice),
          low: Math.min(tickCandleRef.current.low, currentPrice),
          close: currentPrice,
        };

        // Check if we've reached the tick threshold
        if (currentTickCountRef.current >= ticksPerCandle) {
          // Finalize this candle and start a new one
          tickDataRef.current.push({ ...tickCandleRef.current });

          tickCandleRef.current = {
            time: tickTime,
            open: currentPrice,
            high: currentPrice,
            low: currentPrice,
            close: currentPrice,
          };
          currentTickCountRef.current = 1;

          // Update chart with all tick data
          if (tickDataRef.current.length > 0) {
            try {
              candleSeriesRef.current.setData(tickDataRef.current);
            } catch (error) {
              console.error('Error updating tick chart:', error);
            }
          }
        }
      }

      // Always update current candle
      try {
        candleSeriesRef.current.update(tickCandleRef.current);
      } catch (error) {
        console.error('Error updating tick candle:', error);
      }
    }
  }, [livePrice.price, livePrice.isLive, chartMode, ticksPerCandle]);

  // Effect to update bid/ask price lines for forex symbols
  useEffect(() => {
    if (!candleSeriesRef.current || !symbol) return;

    const isForex = symbol.endsWith('.FOREX');

    if (isForex && livePrice.bid && livePrice.ask && livePrice.isLive) {
      const bidPrice = Number(livePrice.bid);
      const askPrice = Number(livePrice.ask);

      // Calculate spread
      const spread = askPrice - bidPrice;
      const spreadPercent = (spread / bidPrice) * 100;

      // Update or create bid line
      if (bidLineRef.current) {
        candleSeriesRef.current.removePriceLine(bidLineRef.current);
      }
      bidLineRef.current = candleSeriesRef.current.createPriceLine({
        price: bidPrice,
        color: '#EF4444',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: `Bid ${bidPrice.toFixed(4)}`,
      });

      // Update or create ask line
      if (askLineRef.current) {
        candleSeriesRef.current.removePriceLine(askLineRef.current);
      }
      askLineRef.current = candleSeriesRef.current.createPriceLine({
        price: askPrice,
        color: '#10B981',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: `Ask ${askPrice.toFixed(4)}`,
      });
    } else {
      // Remove lines if not forex or no data
      if (bidLineRef.current && candleSeriesRef.current) {
        candleSeriesRef.current.removePriceLine(bidLineRef.current);
        bidLineRef.current = null;
      }
      if (askLineRef.current && candleSeriesRef.current) {
        candleSeriesRef.current.removePriceLine(askLineRef.current);
        askLineRef.current = null;
      }
    }
  }, [livePrice.bid, livePrice.ask, livePrice.isLive, symbol]);

  const formatCurrency = (value: number | undefined, currency: string = '$') => {
    if (value === undefined || value === null) return '—';
    return `${currency}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value === null) return '—';
    return `${(value * 100).toFixed(2)}%`;
  };

  // Convert JSE fundamental prices from cents to rands
  const convertJSEPrice = (value: number | undefined): number | undefined => {
    if (value === undefined || value === null) return value;
    const isJSE = symbol?.includes('.JSE');
    return isJSE ? value / 100 : value;
  };

  const handleReplayData = (replayData: ChartDataPoint[]) => {
    if (!candleSeriesRef.current || replayData.length === 0) return;

    try {
      candleSeriesRef.current.setData(replayData);

      // Update volume data for replay
      if (volumeSeriesRef.current) {
        const volumeData = replayData.map((d, index) => {
          const prevClose = index > 0 ? replayData[index - 1].close : d.open;
          const isUp = d.close >= prevClose;
          return {
            time: d.time,
            value: d.volume || 0,
            color: isUp ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)',
          };
        });
        volumeSeriesRef.current.setData(volumeData);
      }

      chartRef.current?.timeScale().fitContent();
    } catch (error) {
      console.error('Error updating replay data:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (!assetData) {
    return (
      <div className="p-6">
        <p className="text-center text-slate-400">Asset not found</p>
      </div>
    );
  }

  const currency = assetData.currency;

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold">{assetData.name}</h1>
              <span className="text-xs bg-slate-700 px-2 py-1 rounded">{assetData.exchange}</span>
              <span className="text-xs bg-slate-700/50 px-2 py-1 rounded text-slate-400">
                {fundamentals?.General?.Sector || assetData.sector}
              </span>
              {fundamentals?.General?.Industry && (
                <span className="text-xs bg-slate-700/30 px-2 py-1 rounded text-slate-500">
                  {fundamentals.General.Industry}
                </span>
              )}
            </div>
            <p className="text-teal-400 font-mono text-sm mt-1">{assetData.symbol}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {fundamentals?.General?.WebURL && (
            <a
              href={fundamentals.General.WebURL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-sm"
            >
              <Globe size={14} />
              Website
              <ExternalLink size={12} />
            </a>
          )}
          <button
            onClick={toggleWatchlistStatus}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              isWatchlisted
                ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                : 'bg-slate-700 hover:bg-slate-600'
            }`}
          >
            <Star size={16} fill={isWatchlisted ? 'currentColor' : 'none'} />
            {isWatchlisted ? 'Watchlisted' : 'Add to Watchlist'}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
            <Bell size={16} />
            Set Alert
          </button>
        </div>
      </div>

      {/* Price Section */}
      <div className="mb-6">
        <div className="flex flex-wrap items-end gap-2 sm:gap-4">
          <span className="text-3xl sm:text-5xl font-bold font-mono">
            {currency} {Number(livePrice.isLive && livePrice.price ? livePrice.price : assetData.price).toFixed(2)}
          </span>
          <PriceChange value={(() => {
            if (livePrice.isLive && livePrice.price && assetData.stats.prevClose > 0) {
              return ((Number(livePrice.price) - assetData.stats.prevClose) / assetData.stats.prevClose) * 100;
            }
            return (livePrice.isLive && livePrice.changePercent !== undefined
              ? livePrice.changePercent
              : assetData.changePercent) || 0;
          })()} size="lg" />
          <span className="text-slate-500 text-sm mb-2">Today</span>
        </div>
        {fundamentals?.Highlights?.WallStreetTargetPrice && (() => {
          // Convert JSE prices from cents to rands
          const isJSE = symbol?.includes('.JSE');
          const targetPrice = isJSE
            ? Number(fundamentals.Highlights.WallStreetTargetPrice) / 100
            : Number(fundamentals.Highlights.WallStreetTargetPrice);
          const currentPrice = Number(livePrice.isLive && livePrice.price ? livePrice.price : assetData.price);
          const upsidePercent = ((targetPrice - currentPrice) / currentPrice * 100).toFixed(1);

          return (
            <div className="mt-2 text-sm text-slate-400">
              Analyst Target:{' '}
              <span className="font-mono text-teal-400">
                {currency}{targetPrice.toFixed(2)}
              </span>
              <span className="ml-2">
                ({upsidePercent}% upside)
              </span>
            </div>
          );
        })()}
      </div>

      {/* Price Chart */}
      <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide">Price Chart</h3>
          <div className="flex flex-wrap items-center gap-2 ml-auto">
            {/* Indicator Toggle Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowIndicatorMenu(!showIndicatorMenu)}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
              >
                <Layers size={14} />
                Indicators
                <ChevronDown size={14} className={`transition-transform ${showIndicatorMenu ? 'rotate-180' : ''}`} />
              </button>
              {showIndicatorMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-slate-700 rounded-lg shadow-lg border border-slate-600 z-20 py-2">
                  <div className="px-3 py-1 text-xs text-slate-400 uppercase font-semibold">Price Overlays</div>
                  <button
                    onClick={() => toggleIndicator('sma50')}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-blue-500 rounded" />
                      <span className="text-sm">SMA 50</span>
                    </div>
                    <div className={`w-4 h-4 rounded border ${indicatorSettings.sma50 ? 'bg-teal-500 border-teal-500' : 'border-slate-500'}`}>
                      {indicatorSettings.sma50 && <span className="text-xs text-white">✓</span>}
                    </div>
                  </button>
                  <button
                    onClick={() => toggleIndicator('sma200')}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-orange-500 rounded" />
                      <span className="text-sm">SMA 200</span>
                    </div>
                    <div className={`w-4 h-4 rounded border ${indicatorSettings.sma200 ? 'bg-teal-500 border-teal-500' : 'border-slate-500'}`}>
                      {indicatorSettings.sma200 && <span className="text-xs text-white">✓</span>}
                    </div>
                  </button>
                  <button
                    onClick={() => toggleIndicator('ema20')}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-purple-500 rounded" />
                      <span className="text-sm">EMA 20</span>
                    </div>
                    <div className={`w-4 h-4 rounded border ${indicatorSettings.ema20 ? 'bg-teal-500 border-teal-500' : 'border-slate-500'}`}>
                      {indicatorSettings.ema20 && <span className="text-xs text-white">✓</span>}
                    </div>
                  </button>
                  <button
                    onClick={() => toggleIndicator('bollinger')}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-cyan-400 rounded" />
                      <span className="text-sm">Bollinger Bands</span>
                    </div>
                    <div className={`w-4 h-4 rounded border ${indicatorSettings.bollinger ? 'bg-teal-500 border-teal-500' : 'border-slate-500'}`}>
                      {indicatorSettings.bollinger && <span className="text-xs text-white">✓</span>}
                    </div>
                  </button>
                  <div className="border-t border-slate-600 my-2" />
                  <div className="px-3 py-1 text-xs text-slate-400 uppercase font-semibold">Oscillators</div>
                  <button
                    onClick={() => toggleIndicator('rsi')}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-amber-500 rounded" />
                      <span className="text-sm">RSI (14)</span>
                    </div>
                    <div className={`w-4 h-4 rounded border ${indicatorSettings.rsi ? 'bg-teal-500 border-teal-500' : 'border-slate-500'}`}>
                      {indicatorSettings.rsi && <span className="text-xs text-white">✓</span>}
                    </div>
                  </button>
                  <button
                    onClick={() => toggleIndicator('macd')}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-600 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-0.5 bg-emerald-500 rounded" />
                      <span className="text-sm">MACD</span>
                    </div>
                    <div className={`w-4 h-4 rounded border ${indicatorSettings.macd ? 'bg-teal-500 border-teal-500' : 'border-slate-500'}`}>
                      {indicatorSettings.macd && <span className="text-xs text-white">✓</span>}
                    </div>
                  </button>
                </div>
              )}
            </div>
            {/* Chart Mode Toggle (for live charts) */}
            {livePrice.isLive && (
              <div className="flex gap-1 p-1 bg-slate-700 rounded-lg">
                <button
                  onClick={() => {
                    setChartMode('time');
                    tickDataRef.current = [];
                    tickCandleRef.current = null;
                    currentTickCountRef.current = 0;
                  }}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    chartMode === 'time'
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  title="Time-based candles (1 minute)"
                >
                  Time
                </button>
                <button
                  onClick={() => setChartMode('tick')}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    chartMode === 'tick'
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  title="Tick-based candles"
                >
                  Tick
                </button>
              </div>
            )}

            {/* Ticks Per Candle (only in tick mode) */}
            {livePrice.isLive && chartMode === 'tick' && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Ticks:</span>
                <select
                  value={ticksPerCandle}
                  onChange={(e) => {
                    setTicksPerCandle(Number(e.target.value));
                    tickDataRef.current = [];
                    tickCandleRef.current = null;
                    currentTickCountRef.current = 0;
                  }}
                  className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-200 focus:outline-none focus:border-blue-500"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            )}

            {/* Timeframe Buttons (only in time mode) */}
            {chartMode === 'time' && (
              <div className="flex overflow-x-auto gap-1 p-1 bg-slate-700 rounded-lg max-w-full">
                {timeframes.map((tf) => (
                  <button
                    key={tf.id}
                    onClick={() => setTimeframe(tf.id)}
                    className={`px-2 sm:px-3 py-1.5 rounded text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                      timeframe === tf.id
                        ? 'bg-teal-500 text-slate-900'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>
            )}

            {/* Replay Mode Button */}
            {!livePrice.isLive && historicalChartData.length > 0 && chartMode === 'time' && (
              <button
                onClick={() => setReplayMode(!replayMode)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  replayMode
                    ? 'bg-purple-500 text-white'
                    : 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30'
                }`}
                title="Replay historical price movements"
              >
                <FastForward size={14} />
                {replayMode ? 'Exit Replay' : 'Replay Mode'}
              </button>
            )}

            {/* Chart Status Indicators */}
            <div className="flex items-center gap-3">
              {/* Live Indicator */}
              {isLiveChart && livePrice.isLive && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wide">LIVE</span>
                    <span className="text-xs text-emerald-300/70">
                      ${Number(livePrice.price).toFixed(2)}
                    </span>
                  </div>
                  {/* Bid/Ask Spread for Forex */}
                  {symbol?.endsWith('.FOREX') && livePrice.bid && livePrice.ask && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div className="flex items-center gap-1 text-xs">
                        <span className="text-red-400">Bid: {Number(livePrice.bid).toFixed(4)}</span>
                        <span className="text-slate-500">|</span>
                        <span className="text-emerald-400">Ask: {Number(livePrice.ask).toFixed(4)}</span>
                      </div>
                      <div className="text-[10px] text-blue-300/70 bg-blue-500/20 px-1.5 py-0.5 rounded">
                        Spread: {((Number(livePrice.ask) - Number(livePrice.bid)) * 10000).toFixed(1)} pips
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Active Indicators Legend */}
        {(indicatorSettings.sma50 || indicatorSettings.sma200 || indicatorSettings.ema20 || indicatorSettings.bollinger) && (
          <div className="flex flex-wrap gap-4 mb-3 text-xs">
            {indicatorSettings.sma50 && (
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-blue-500 rounded" />
                <span className="text-slate-400">SMA 50</span>
              </div>
            )}
            {indicatorSettings.sma200 && (
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-orange-500 rounded" />
                <span className="text-slate-400">SMA 200</span>
              </div>
            )}
            {indicatorSettings.ema20 && (
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-purple-500 rounded" />
                <span className="text-slate-400">EMA 20</span>
              </div>
            )}
            {indicatorSettings.bollinger && (
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-cyan-400 rounded" />
                <span className="text-slate-400">Bollinger Bands</span>
              </div>
            )}
          </div>
        )}

        {/* Replay Controls */}
        {replayMode && historicalChartData.length > 0 && (
          <ChartReplayControls
            historicalData={historicalChartData}
            onReplayData={handleReplayData}
            onClose={() => {
              setReplayMode(false);
              // Reload full chart data
              loadChartData();
            }}
          />
        )}

        {isTV ? (
          <TradingViewAdvancedChart
            symbol={symbol ?? ''}
            defaultTimeframe={timeframe}
            height={520}
          />
        ) : (
          <div className="relative">
            {chartLoading && (
              <div className="absolute inset-0 bg-slate-800/80 flex items-center justify-center z-10">
                <LoadingSpinner />
              </div>
            )}
            <div ref={chartContainerRef} className="w-full min-h-[320px]" />
          </div>
        )}
      </div>

      {/* Strategy Signal Card — v2 users only, between chart and Technical Analysis */}
      {hasStrategyFeature && (
        <div className="mb-6">
          {(strategyBreakdownLoading || strategyRefreshing) && routerStrategyBreakdown === null ? (
            <StrategySignalCardSkeleton />
          ) : strategyBreakdownError ? (
            <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-5 flex items-center justify-between">
              <span className="text-sm text-slate-400">
                Strategy analysis unavailable — try refreshing
              </span>
              <button
                onClick={() => forceRefreshStrategyBreakdown()}
                disabled={strategyRefreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors text-slate-300 disabled:opacity-50"
              >
                <RefreshCw size={12} className={strategyRefreshing ? 'animate-spin' : ''} />
                {strategyRefreshing ? 'Refreshing…' : 'Retry'}
              </button>
            </div>
          ) : strategyBreakdown ? (
            <StrategySignalCard
              symbol={symbol ?? ''}
              data={strategyBreakdown}
              onRefresh={() => forceRefreshStrategyBreakdown()}
              isLoading={strategyRefreshing}
            />
          ) : null}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Key Stats & Trading Data */}
        <div className="space-y-6">
          {/* Key Statistics */}
          <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4">Trading Data</h3>
            <div className="space-y-1">
              <StatRow label="Open" value={formatCurrency(assetData.stats.open, currency)} />
              <StatRow
                label={livePrice.isLive ? "Session High" : "High"}
                value={formatCurrency(sessionHigh ?? assetData.stats.high, currency)}
                highlight
              />
              <StatRow
                label={livePrice.isLive ? "Session Low" : "Low"}
                value={formatCurrency(sessionLow ?? assetData.stats.low, currency)}
              />
              <StatRow label="Prev Close" value={formatCurrency(assetData.stats.prevClose, currency)} />
              <div className="py-2" />
              <StatRow
                label="52W High"
                value={formatCurrency(convertJSEPrice(fundamentals?.Technicals?.['52WeekHigh']) || assetData.stats.week52High, currency)}
                highlight
              />
              <StatRow
                label="52W Low"
                value={formatCurrency(convertJSEPrice(fundamentals?.Technicals?.['52WeekLow']) || assetData.stats.week52Low, currency)}
              />
              <div className="py-2" />
              <StatRow label="Volume" value={assetData.stats.volume} />
              {fundamentals?.Technicals?.['50DayMA'] && (
                <StatRow
                  label="50 Day MA"
                  value={formatCurrency(convertJSEPrice(fundamentals.Technicals['50DayMA']), currency)}
                  tooltip="50-day moving average"
                />
              )}
              {fundamentals?.Technicals?.['200DayMA'] && (
                <StatRow
                  label="200 Day MA"
                  value={formatCurrency(convertJSEPrice(fundamentals.Technicals['200DayMA']), currency)}
                  tooltip="200-day moving average"
                />
              )}
            </div>
          </div>

          {/* Price Alerts */}
          {livePrice.isLive && (
            <PriceAlerts
              symbol={symbol || ''}
              currentPrice={Number(livePrice.price || assetData.price)}
            />
          )}

          {/* Technical Indicators */}
          <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
            <button
              onClick={() => toggleSection('technicals')}
              className="w-full flex items-center justify-between mb-4"
            >
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-2">
                <Activity size={16} className="text-purple-400" />
                Technical Indicators
              </h3>
              {expandedSections.technicals ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {expandedSections.technicals && (
              <div className="space-y-4">
                {/* Overall Signal */}
                <div className="bg-slate-700/30 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-2">Overall Signal</p>
                  <div className="flex items-center gap-2">
                    {technicals?.signal === 'bullish' ? (
                      <>
                        <TrendingUp size={20} className="text-emerald-400" />
                        <span className="text-lg font-bold text-emerald-400">BULLISH</span>
                      </>
                    ) : technicals?.signal === 'bearish' ? (
                      <>
                        <TrendingDown size={20} className="text-red-400" />
                        <span className="text-lg font-bold text-red-400">BEARISH</span>
                      </>
                    ) : (
                      <span className="text-lg font-bold text-slate-400">NEUTRAL</span>
                    )}
                  </div>
                </div>

                {/* Individual Indicators */}
                <div className="space-y-3">
                  {technicals?.rsi !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">RSI (14)</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-sm ${
                          technicals.rsi < 30 ? 'text-emerald-400' :
                          technicals.rsi > 70 ? 'text-red-400' : 'text-slate-200'
                        }`}>
                          {technicals.rsi.toFixed(2)}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-400">
                          {technicals.rsi < 30 ? 'Oversold' : technicals.rsi > 70 ? 'Overbought' : 'Neutral'}
                        </span>
                      </div>
                    </div>
                  )}
                  {technicals?.macd && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">MACD</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-mono text-sm ${
                          technicals.macd.histogram > 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {technicals.macd.histogram.toFixed(3)}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-400">
                          {technicals.macd.histogram > 0 ? 'Bullish' : 'Bearish'}
                        </span>
                      </div>
                    </div>
                  )}
                  {technicals?.sma50 && technicals?.sma200 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-400">SMA Cross</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          technicals.sma50 > technicals.sma200
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {technicals.sma50 > technicals.sma200 ? '🟢 Golden Cross' : '🔴 Death Cross'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* TradingView TA Analysis Card (v30-1-5) */}
          <TaAnalysisCard symbol={symbol ?? ''} interval={timeframe === '4H' ? '4h' : timeframe === '1H' ? '1h' : '1d'} />
        </div>

        {/* Middle Column - Fundamentals & Valuation */}
        <div className="space-y-6">
          {/* Fundamental Highlights */}
          <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
            <button
              onClick={() => toggleSection('fundamentals')}
              className="w-full flex items-center justify-between mb-4"
            >
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-2">
                <BarChart3 size={16} className="text-teal-400" />
                Fundamentals
              </h3>
              {expandedSections.fundamentals ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {expandedSections.fundamentals && (
              fundamentalsLoading ? (
                <div className="py-8 flex justify-center">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-1">
                  <StatRow
                    label="Market Cap"
                    value={fundamentals?.Highlights?.MarketCapitalizationMln
                      ? formatLargeNumber(fundamentals.Highlights.MarketCapitalizationMln * 1000000)
                      : '—'}
                  />
                  <StatRow
                    label="P/E Ratio"
                    value={fundamentals?.Highlights?.PERatio?.toFixed(2) || '—'}
                    tooltip="Price to Earnings ratio"
                  />
                  <StatRow
                    label="PEG Ratio"
                    value={fundamentals?.Highlights?.PEGRatio?.toFixed(2) || '—'}
                    tooltip="Price/Earnings to Growth ratio"
                  />
                  <StatRow
                    label="EPS"
                    value={formatCurrency(fundamentals?.Highlights?.EarningsShare, currency)}
                    tooltip="Earnings per share"
                  />
                  <div className="py-2" />
                  <StatRow
                    label="Revenue (TTM)"
                    value={fundamentals?.Highlights?.RevenueTTM
                      ? formatLargeNumber(fundamentals.Highlights.RevenueTTM)
                      : '—'}
                  />
                  <StatRow
                    label="EBITDA"
                    value={fundamentals?.Highlights?.EBITDA
                      ? formatLargeNumber(fundamentals.Highlights.EBITDA)
                      : '—'}
                  />
                  <StatRow
                    label="Profit Margin"
                    value={formatPercent(fundamentals?.Highlights?.ProfitMargin)}
                  />
                  <StatRow
                    label="Operating Margin"
                    value={formatPercent(fundamentals?.Highlights?.OperatingMarginTTM)}
                  />
                </div>
              )
            )}
          </div>

          {/* Valuation Metrics */}
          <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
            <button
              onClick={() => toggleSection('valuation')}
              className="w-full flex items-center justify-between mb-4"
            >
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-2">
                <DollarSign size={16} className="text-amber-400" />
                Valuation
              </h3>
              {expandedSections.valuation ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {expandedSections.valuation && (
              <div className="space-y-1">
                <StatRow
                  label="Trailing P/E"
                  value={fundamentals?.Valuation?.TrailingPE?.toFixed(2) || '—'}
                />
                <StatRow
                  label="Forward P/E"
                  value={fundamentals?.Valuation?.ForwardPE?.toFixed(2) || '—'}
                />
                <StatRow
                  label="Price/Sales"
                  value={fundamentals?.Valuation?.PriceSalesTTM?.toFixed(2) || '—'}
                />
                <StatRow
                  label="Price/Book"
                  value={fundamentals?.Valuation?.PriceBookMRQ?.toFixed(2) || '—'}
                />
                <div className="py-2" />
                <StatRow
                  label="EV/Revenue"
                  value={fundamentals?.Valuation?.EnterpriseValueRevenue?.toFixed(2) || '—'}
                />
                <StatRow
                  label="EV/EBITDA"
                  value={fundamentals?.Valuation?.EnterpriseValueEbitda?.toFixed(2) || '—'}
                />
                <div className="py-2" />
                <StatRow
                  label="Book Value"
                  value={formatCurrency(convertJSEPrice(fundamentals?.Highlights?.BookValue), currency)}
                />
                <StatRow
                  label="Dividend Yield"
                  value={fundamentals?.Highlights?.DividendYield
                    ? `${(fundamentals.Highlights.DividendYield * 100).toFixed(2)}%`
                    : '—'}
                  highlight={!!fundamentals?.Highlights?.DividendYield}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Analyst Ratings, Company Profile, Quick Actions */}
        <div className="space-y-6">
          {/* AI Analysis History */}
          <AIAnalysisHistory symbol={symbol || ''} />

          {/* Latest News */}
          <AssetNews symbol={symbol || ''} limit={5} />

          {/* Analyst Ratings */}
          {fundamentals?.AnalystRatings && (
            <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
              <button
                onClick={() => toggleSection('analysts')}
                className="w-full flex items-center justify-between mb-4"
              >
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-2">
                  <Users size={16} className="text-blue-400" />
                  Analyst Ratings
                </h3>
                {expandedSections.analysts ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {expandedSections.analysts && (
                <div className="space-y-4">
                  {/* Rating Bar */}
                  <div className="flex gap-1 h-6">
                    {fundamentals.AnalystRatings.StrongBuy && fundamentals.AnalystRatings.StrongBuy > 0 && (
                      <div
                        className="bg-emerald-500 rounded-l"
                        style={{ flex: fundamentals.AnalystRatings.StrongBuy }}
                        title={`Strong Buy: ${fundamentals.AnalystRatings.StrongBuy}`}
                      />
                    )}
                    {fundamentals.AnalystRatings.Buy && fundamentals.AnalystRatings.Buy > 0 && (
                      <div
                        className="bg-emerald-400"
                        style={{ flex: fundamentals.AnalystRatings.Buy }}
                        title={`Buy: ${fundamentals.AnalystRatings.Buy}`}
                      />
                    )}
                    {fundamentals.AnalystRatings.Hold && fundamentals.AnalystRatings.Hold > 0 && (
                      <div
                        className="bg-slate-400"
                        style={{ flex: fundamentals.AnalystRatings.Hold }}
                        title={`Hold: ${fundamentals.AnalystRatings.Hold}`}
                      />
                    )}
                    {fundamentals.AnalystRatings.Sell && fundamentals.AnalystRatings.Sell > 0 && (
                      <div
                        className="bg-red-400"
                        style={{ flex: fundamentals.AnalystRatings.Sell }}
                        title={`Sell: ${fundamentals.AnalystRatings.Sell}`}
                      />
                    )}
                    {fundamentals.AnalystRatings.StrongSell && fundamentals.AnalystRatings.StrongSell > 0 && (
                      <div
                        className="bg-red-500 rounded-r"
                        style={{ flex: fundamentals.AnalystRatings.StrongSell }}
                        title={`Strong Sell: ${fundamentals.AnalystRatings.StrongSell}`}
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 text-xs text-center">
                    <div>
                      <div className="text-emerald-500 font-bold">{fundamentals.AnalystRatings.StrongBuy || 0}</div>
                      <div className="text-slate-500">Strong Buy</div>
                    </div>
                    <div>
                      <div className="text-emerald-400 font-bold">{fundamentals.AnalystRatings.Buy || 0}</div>
                      <div className="text-slate-500">Buy</div>
                    </div>
                    <div>
                      <div className="text-slate-400 font-bold">{fundamentals.AnalystRatings.Hold || 0}</div>
                      <div className="text-slate-500">Hold</div>
                    </div>
                    <div>
                      <div className="text-red-400 font-bold">{fundamentals.AnalystRatings.Sell || 0}</div>
                      <div className="text-slate-500">Sell</div>
                    </div>
                    <div>
                      <div className="text-red-500 font-bold">{fundamentals.AnalystRatings.StrongSell || 0}</div>
                      <div className="text-slate-500">Strong Sell</div>
                    </div>
                  </div>
                  {fundamentals.AnalystRatings.TargetPrice && (
                    <div className="pt-2 border-t border-slate-700">
                      <StatRow
                        label="Target Price"
                        value={formatCurrency(convertJSEPrice(fundamentals.AnalystRatings.TargetPrice), currency)}
                        highlight
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Company Profile */}
          <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
            <button
              onClick={() => toggleSection('profile')}
              className="w-full flex items-center justify-between mb-4"
            >
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-2">
                <Building2 size={16} className="text-slate-400" />
                Company Profile
              </h3>
              {expandedSections.profile ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {expandedSections.profile && (
              <div className="space-y-3">
                {fundamentals?.General?.Description && (
                  <p className="text-sm text-slate-400 leading-relaxed line-clamp-4">
                    {fundamentals.General.Description}
                  </p>
                )}
                <div className="space-y-1 pt-2">
                  {fundamentals?.General?.CountryName && (
                    <StatRow label="Country" value={fundamentals.General.CountryName} />
                  )}
                  {fundamentals?.General?.FullTimeEmployees && (
                    <StatRow
                      label="Employees"
                      value={fundamentals.General.FullTimeEmployees.toLocaleString()}
                    />
                  )}
                  {fundamentals?.General?.IPODate && (
                    <StatRow
                      label="IPO Date"
                      value={new Date(fundamentals.General.IPODate).toLocaleDateString()}
                    />
                  )}
                  {fundamentals?.SharesStats?.SharesOutstanding && (
                    <StatRow
                      label="Shares Outstanding"
                      value={formatLargeNumber(fundamentals.SharesStats.SharesOutstanding)}
                    />
                  )}
                  {fundamentals?.SharesStats?.PercentInsiders !== undefined && (
                    <StatRow
                      label="Insider Ownership"
                      value={`${fundamentals.SharesStats.PercentInsiders.toFixed(2)}%`}
                    />
                  )}
                  {fundamentals?.SharesStats?.PercentInstitutions !== undefined && (
                    <StatRow
                      label="Institutional"
                      value={`${fundamentals.SharesStats.PercentInstitutions.toFixed(2)}%`}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-4">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/alerts')}
                className="w-full px-4 py-3 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Set Price Alert
              </button>
              <button
                onClick={() => navigate('/portfolio')}
                className="w-full px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                Add to Portfolio
              </button>
              <button
                onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(assetData.symbol)}`)}
                className="w-full px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Activity size={16} />
                AI Analysis
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDetail;
